import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { baseUrls, cookieNames, pageRedirect } from '@monorepo/common';

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: baseUrls.api,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios instance for jobs API
export const jobApiClient = axios.create({
  baseURL: baseUrls.jobApi,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
const requestInterceptor = (config: any) => {
  const token = cookies.get(cookieNames.bevnet_token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const requestErrorInterceptor = (error: any) => {
  return Promise.reject(error);
};

apiClient.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
jobApiClient.interceptors.request.use(requestInterceptor, requestErrorInterceptor);

// Response interceptor for error handling
const responseSuccessInterceptor = (response: AxiosResponse) => {
  return response;
};

// Invoice pages are paid without signing in, so their errors must be shown, not turned into a login redirect.
const isInvoicePage = () => /^\/invoice(?:\/|$)/i.test(window.location.pathname);

const responseErrorInterceptor = (error: AxiosError<any>) => {
  const token = cookies.get(cookieNames.bevnet_token);

  if (!token && !isInvoicePage()) {
    pageRedirect({ token, delay: 0 });
    return Promise.reject(error);
  }

  // Handle 401 Unauthorized
  if (error.response?.status === 401) {
    toast.error("Sorry, you don't have authorization");
    pageRedirect({ token });
    return Promise.reject(error);
  }

  // Handle 403 Forbidden
  if (error.response?.status === 403) {
    toast.error('Sorry, you do not have permission');
    pageRedirect({ token });
    return Promise.reject(error);
  }

  // Handle 400 Bad Request
  if (error.response?.status === 400) {
    const data = error.response.data;
    if (data?.errors) {
      const errorMessages = Object.values(data.errors).flat().join('\n');
      toast.error(errorMessages);
    } else if (data?.error) {
      toast.error(data.error);
    } else if (typeof data === 'string') {
      toast.error(data);
    }
    return Promise.reject(error);
  }

  // Handle 409 Conflict
  if (error.response?.status === 409) {
    const message = error.response.data?.error;
    if (message) {
      toast.error(message, { id: 'conflict-error' });
    }
    return Promise.reject(error);
  }

  // Handle 500 Internal Server Error
  if (error.response?.status === 500) {
    toast.error('Internal server Error!');
    return Promise.reject(error);
  }

  // Handle other errors
  if (error.response?.data?.error) {
    toast.error(error.response.data.error);
  } else if (error.message) {
    toast.error(error.message);
  }

  return Promise.reject(error);
};

apiClient.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);
jobApiClient.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);

// Helper function for making requests with better typing
export async function request<T = any>(
  config: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.request<T>(config);
  return response.data;
}

// Helper function for making requests with jobApiClient
export async function jobRequest<T = any>(
  config: AxiosRequestConfig
): Promise<T> {
  const response = await jobApiClient.request<T>(config);
  return response.data;
}

// Convenience methods
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'GET', url }),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'POST', url, data }),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PUT', url, data }),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'DELETE', url }),
};

// Job API convenience methods (uses jobApiClient with correct base URL)
export const jobApi = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    jobRequest<T>({ ...config, method: 'GET', url }),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    jobRequest<T>({ ...config, method: 'POST', url, data }),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    jobRequest<T>({ ...config, method: 'PUT', url, data }),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    jobRequest<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    jobRequest<T>({ ...config, method: 'DELETE', url }),
};
